import { useCallback, useEffect, useRef, useState } from 'react';
import type { RevisionLike } from '../EditorWorkspace/workflow';
import { requestRevisionFile } from './revisionMedia';
import { isAbortError, readableError } from './reviewModel';

export interface RevisionFileState {
  status: 'loading' | 'ready' | 'error';
  /** 0–1 while loading, when the server sends a length. */
  progress: number | null;
  url?: string;
  error?: string;
}

export interface RevisionFiles {
  states: Record<string, RevisionFileState | undefined>;
  /** Resolves with a blob URL; loads the file once and reuses it after. */
  load: (revision: RevisionLike) => Promise<string>;
  cancel: (revisionId: string) => void;
  /** The revision on screen; its file is never evicted. */
  pin: (revisionId: string | null) => void;
}

const cancelledError = () => {
  const error = new Error('Loading was cancelled.');
  error.name = 'AbortError';
  return error;
};

/**
 * Loaded revision files, shared by the player and the download buttons so a
 * cut that was watched downloads instantly (and the other way round). Keeps
 * at most `maxCached` files; they can be gigabytes, so older ones are
 * released, and everything is released when the page closes.
 */
export function useRevisionFiles(getToken: () => Promise<string | null>, maxCached = 2): RevisionFiles {
  const [states, setStates] = useState<Record<string, RevisionFileState | undefined>>({});
  // Insertion order is least recently used first.
  const urls = useRef(new Map<string, string>());
  const inflight = useRef(new Map<string, { promise: Promise<string>; abort: () => void }>());
  const pinned = useRef<string | null>(null);
  const mounted = useRef(true);

  const update = useCallback((id: string, next: RevisionFileState | undefined) => {
    if (!mounted.current) return;
    setStates((previous) => ({ ...previous, [id]: next }));
  }, []);

  useEffect(() => {
    mounted.current = true;
    const urlMap = urls.current;
    const inflightMap = inflight.current;
    return () => {
      mounted.current = false;
      inflightMap.forEach((request) => request.abort());
      inflightMap.clear();
      urlMap.forEach((url) => URL.revokeObjectURL(url));
      urlMap.clear();
    };
  }, []);

  const evict = useCallback(() => {
    while (urls.current.size > maxCached) {
      const victim = Array.from(urls.current.keys()).find((id) => id !== pinned.current);
      if (!victim) break;
      const url = urls.current.get(victim);
      urls.current.delete(victim);
      if (url) URL.revokeObjectURL(url);
      update(victim, undefined);
    }
  }, [maxCached, update]);

  const load = useCallback(
    (revision: RevisionLike): Promise<string> => {
      const id = revision.id;
      const cached = urls.current.get(id);
      if (cached) {
        urls.current.delete(id);
        urls.current.set(id, cached);
        return Promise.resolve(cached);
      }
      const running = inflight.current.get(id);
      if (running) return running.promise;
      const previewUrl = revision.previewUrl;
      if (!previewUrl) return Promise.reject(new Error('This version has no file yet.'));

      let cancelled = false;
      let abortRequest: (() => void) | null = null;
      update(id, { status: 'loading', progress: null });

      const promise = (async () => {
        const token = await getToken();
        if (cancelled) throw cancelledError();
        if (!token) throw new Error('Your session has expired. Sign in again to watch this version.');
        const request = requestRevisionFile(previewUrl, token, ({ loaded, total }) => {
          update(id, { status: 'loading', progress: total ? Math.min(1, loaded / total) : null });
        });
        abortRequest = request.abort;
        const blob = await request.promise;
        const url = URL.createObjectURL(blob);
        if (!mounted.current) {
          URL.revokeObjectURL(url);
          throw cancelledError();
        }
        urls.current.set(id, url);
        update(id, { status: 'ready', progress: 1, url });
        evict();
        return url;
      })();

      inflight.current.set(id, {
        promise,
        abort: () => {
          cancelled = true;
          abortRequest?.();
        },
      });
      promise.then(
        () => {
          inflight.current.delete(id);
        },
        (error) => {
          inflight.current.delete(id);
          if (isAbortError(error)) {
            update(id, undefined);
            return;
          }
          update(id, {
            status: 'error',
            progress: null,
            error: readableError(error, 'This version could not be loaded. Check your connection and try again.'),
          });
        }
      );
      return promise;
    },
    [getToken, update, evict]
  );

  const cancel = useCallback((revisionId: string) => {
    inflight.current.get(revisionId)?.abort();
  }, []);

  const pin = useCallback((revisionId: string | null) => {
    pinned.current = revisionId;
  }, []);

  return { states, load, cancel, pin };
}
